import type {
  AchievementView,
  AiAvailability,
  CreateDailyTaskInput,
  CreateNoteInput,
  CreateProblemInput,
  CreateResourceInput,
  CreateSessionInput,
  CreateSolutionInput,
  DailyTask,
  DailyView,
  DashboardView,
  HeatmapDay,
  LearningSession,
  LoginInput,
  LoginResult,
  MonthlyReport,
  Note,
  ProblemRecommendation,
  ProblemView,
  RatingHistoryEntry,
  Solution,
  SyncChangeSet,
  SyncPullResponse,
  SyncPushResponse,
  Tag,
  TopicAnalysis,
  TopicResource,
  TopicView,
  UpdateDailyTaskInput,
  UpdateNoteInput,
  UpdateProblemInput,
  UpdateProfileInput,
  UpdateSessionInput,
  UpdateSolutionInput,
  UpdateTopicInput,
  UserProfile,
  WeeklyReport,
} from '@algo/shared';

export interface ApiClientConfig {
  /** e.g. `http://localhost:4000` on desktop, `http://192.168.1.20:4000` over LAN. */
  baseUrl: string;
  /** Static token shared with the backend `.env`. */
  token: string;
  /** Abort a request after this many ms. Keeps mobile from hanging off-network. */
  timeoutMs?: number;
  fetchImpl?: typeof fetch;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(message: string, status: number, code: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** True when the backend was unreachable rather than unhappy. */
  get isOffline(): boolean {
    return this.status === 0;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
}

function buildUrl(
  baseUrl: string,
  path: string,
  query?: RequestOptions['query'],
): string {
  const trimmed = baseUrl.replace(/\/+$/, '');
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === null || value === '') continue;
    search.append(key, String(value));
  }
  const qs = search.toString();
  return `${trimmed}${path}${qs ? `?${qs}` : ''}`;
}

export class ApiClient {
  private config: ApiClientConfig;

  constructor(config: ApiClientConfig) {
    this.config = config;
  }

  /** Re-point the client at a different host or token (used by mobile pairing). */
  configure(patch: Partial<ApiClientConfig>): void {
    this.config = { ...this.config, ...patch };
  }

  get baseUrl(): string {
    return this.config.baseUrl;
  }

  private async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { method = 'GET', body, query, signal } = options;
    const doFetch = this.config.fetchImpl ?? globalThis.fetch;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), this.config.timeoutMs ?? 15_000);

    if (signal) {
      if (signal.aborted) controller.abort();
      else signal.addEventListener('abort', () => controller.abort(), { once: true });
    }

    // Only declare a JSON body when there actually is one: DELETEs and the
    // body-less POSTs would otherwise arrive as `content-type: application/json`
    // with an empty payload, which Fastify rejects before reaching the route.
    const headers: Record<string, string> = { 'x-api-token': this.config.token };
    if (body !== undefined) headers['content-type'] = 'application/json';

    let response: Response;
    try {
      response = await doFetch(buildUrl(this.config.baseUrl, path, query), {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: controller.signal,
      });
    } catch (error) {
      throw new ApiError(
        error instanceof Error && error.name === 'AbortError'
          ? 'Хүсэлтийн хугацаа дууслаа — desktop backend ажиллаж байна уу?'
          : 'Backend-тэй холбогдож чадсангүй.',
        0,
        'NETWORK_ERROR',
        error,
      );
    } finally {
      clearTimeout(timeout);
    }

    if (response.status === 204) return undefined as T;

    const text = await response.text();
    const payload: unknown = text.length > 0 ? safeParse(text) : null;

    if (!response.ok) {
      const problem = payload as { message?: string; code?: string; details?: unknown } | null;
      throw new ApiError(
        problem?.message ?? `Хүсэлт ${response.status} кодоор амжилтгүй боллоо`,
        response.status,
        problem?.code ?? 'HTTP_ERROR',
        problem?.details,
      );
    }

    return payload as T;
  }

  /* ------------------------------- meta -------------------------------- */

  health = () =>
    this.request<{ status: 'ok'; name: string; version: string; serverTime: string }>(
      '/api/health',
    );

  /**
   * Exchanges the username and password for the API token. Signing in is the
   * only way a client obtains a token, so the token never has to be copied by
   * hand or baked into a bundle.
   */
  login = (input: LoginInput) =>
    this.request<LoginResult>('/api/auth/login', { method: 'POST', body: input });

  /* ------------------------------ profile ------------------------------ */

  getProfile = () => this.request<UserProfile>('/api/profile');
  updateProfile = (input: UpdateProfileInput) =>
    this.request<UserProfile>('/api/profile', { method: 'PATCH', body: input });

  /* ----------------------------- dashboard ----------------------------- */

  getDashboard = () => this.request<DashboardView>('/api/dashboard');

  /* ------------------------------- topics ------------------------------ */

  listTopics = () => this.request<TopicView[]>('/api/topics');
  getTopic = (id: string) => this.request<TopicView>(`/api/topics/${id}`);
  updateTopic = (id: string, input: UpdateTopicInput) =>
    this.request<TopicView>(`/api/topics/${id}`, { method: 'PATCH', body: input });
  addResource = (topicId: string, input: CreateResourceInput) =>
    this.request<TopicResource>(`/api/topics/${topicId}/resources`, {
      method: 'POST',
      body: input,
    });
  deleteResource = (id: string) =>
    this.request<void>(`/api/resources/${id}`, { method: 'DELETE' });

  /* ------------------------------ problems ----------------------------- */

  listProblems = (query?: {
    q?: string;
    platform?: string;
    difficulty?: string;
    topicId?: string;
    take?: number;
    skip?: number;
  }) => this.request<{ items: ProblemView[]; total: number }>('/api/problems', { query });
  getProblem = (id: string) => this.request<ProblemView>(`/api/problems/${id}`);
  createProblem = (input: CreateProblemInput) =>
    this.request<ProblemView>('/api/problems', { method: 'POST', body: input });
  updateProblem = (id: string, input: UpdateProblemInput) =>
    this.request<ProblemView>(`/api/problems/${id}`, { method: 'PATCH', body: input });
  deleteProblem = (id: string) =>
    this.request<void>(`/api/problems/${id}`, { method: 'DELETE' });

  /* ----------------------------- solutions ----------------------------- */

  addSolution = (problemId: string, input: CreateSolutionInput) =>
    this.request<Solution>(`/api/problems/${problemId}/solutions`, {
      method: 'POST',
      body: input,
    });
  updateSolution = (id: string, input: UpdateSolutionInput) =>
    this.request<Solution>(`/api/solutions/${id}`, { method: 'PATCH', body: input });
  deleteSolution = (id: string) =>
    this.request<void>(`/api/solutions/${id}`, { method: 'DELETE' });

  /* ------------------------------- daily ------------------------------- */

  getDaily = (date?: string) => this.request<DailyView>('/api/daily', { query: { date } });
  createTask = (input: CreateDailyTaskInput) =>
    this.request<DailyTask>('/api/daily/tasks', { method: 'POST', body: input });
  updateTask = (id: string, input: UpdateDailyTaskInput) =>
    this.request<DailyTask>(`/api/daily/tasks/${id}`, { method: 'PATCH', body: input });
  deleteTask = (id: string) =>
    this.request<void>(`/api/daily/tasks/${id}`, { method: 'DELETE' });

  /* ------------------------------ sessions ----------------------------- */

  listSessions = (query?: { from?: string; to?: string; topicId?: string; take?: number }) =>
    this.request<LearningSession[]>('/api/sessions', { query });
  createSession = (input: CreateSessionInput) =>
    this.request<LearningSession>('/api/sessions', { method: 'POST', body: input });
  updateSession = (id: string, input: UpdateSessionInput) =>
    this.request<LearningSession>(`/api/sessions/${id}`, { method: 'PATCH', body: input });
  deleteSession = (id: string) =>
    this.request<void>(`/api/sessions/${id}`, { method: 'DELETE' });

  /* -------------------------------- notes ------------------------------ */

  listNotes = (query?: { q?: string; tag?: string; category?: string; topicId?: string }) =>
    this.request<Note[]>('/api/notes', { query });
  getNote = (id: string) => this.request<Note>(`/api/notes/${id}`);
  createNote = (input: CreateNoteInput) =>
    this.request<Note>('/api/notes', { method: 'POST', body: input });
  updateNote = (id: string, input: UpdateNoteInput) =>
    this.request<Note>(`/api/notes/${id}`, { method: 'PATCH', body: input });
  deleteNote = (id: string) => this.request<void>(`/api/notes/${id}`, { method: 'DELETE' });
  listTags = () => this.request<Tag[]>('/api/tags');

  /* ---------------------------- achievements --------------------------- */

  listAchievements = () => this.request<AchievementView[]>('/api/achievements');

  /* ----------------------------- statistics ---------------------------- */

  weeklyReport = (period?: string, refresh = false) =>
    this.request<WeeklyReport>('/api/stats/weekly', { query: { period, refresh } });
  monthlyReport = (period?: string, refresh = false) =>
    this.request<MonthlyReport>('/api/stats/monthly', { query: { period, refresh } });
  ratingHistory = () => this.request<RatingHistoryEntry[]>('/api/stats/rating-history');
  heatmap = (days = 365) => this.request<HeatmapDay[]>('/api/stats/heatmap', { query: { days } });

  /* -------------------------------- sync ------------------------------- */

  syncPull = (since: string | null, deviceId: string) =>
    this.request<SyncPullResponse>('/api/sync/pull', {
      method: 'POST',
      body: { since, deviceId },
    });
  syncPush = (changes: SyncChangeSet, lastSyncedAt: string | null, deviceId: string) =>
    this.request<SyncPushResponse>('/api/sync/push', {
      method: 'POST',
      body: { changes, lastSyncedAt, deviceId },
    });

  /* --------------------------------- ai -------------------------------- */

  aiStatus = () => this.request<AiAvailability>('/api/ai/status');
  aiWeakTopics = () => this.request<TopicAnalysis[]>('/api/ai/weak-topics', { method: 'POST' });
  aiRecommendations = (count = 5) =>
    this.request<ProblemRecommendation[]>('/api/ai/recommendations', {
      method: 'POST',
      query: { count },
    });
  aiExplain = (solutionId: string) =>
    this.request<{ explanation: string }>(`/api/ai/explain/${solutionId}`, { method: 'POST' });
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
}

export function createApiClient(config: ApiClientConfig): ApiClient {
  return new ApiClient(config);
}
